import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import { KYCLevel1 } from '../types/kyc';
import { ipfsUriToUrl } from '../utils/ipfs';
import ImagePreview from './ImagePreview';

interface KYCLevel1ReviewFormProps {
  isWhitelisted:boolean;
  data: KYCLevel1;
  status?: number; // KYC status
  onReview?: (approved: boolean, comments?: string) => Promise<void>;
  submitting?: boolean;
  canReview?: boolean; // Has permission to review
  onReset?: () => Promise<void>; 
  canReset?: boolean; // Has permission to reset
  reviewStatus?: 'pending' | 'approved' | 'rejected';
}

const KYCLevel1ReviewForm: React.FC<KYCLevel1ReviewFormProps> = ({
  isWhitelisted,
  data,
  status,
  onReview,
  submitting = false,
  canReview = false,
  onReset, 
  canReset = false,
  reviewStatus
}) => {
  const [comments, setComments] = React.useState<string>('');
  const [idFrontPreview, setIdFrontPreview] = useState<string | null>(null);
  const [idBackPreview, setIdBackPreview] = useState<string | null>(null);
  const handleReview = async (approved: boolean) => {
    if (onReview) {
      await onReview(approved, comments);
    }
  };
  const handleReset = async (approved: boolean) => {
    if (onReset) {
      await onReset();
    }
  };
  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'warning';
    }
  };

  const getStatusText = (status?: string) => {
    switch (status) {
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      default: return 'Pending';
    }
  };

  const getGenderText = (gender: string) => {
    switch (gender) {
      case 'male': return 'Male';
      case 'female': return 'Female';
      case 'other': return 'Other';
      default: return gender;
    }
  };

  const getIdTypeText = (idType: string) => {
    switch (idType) {
      case 'id_card': return 'ID Card';
      case 'passport': return 'Passport';
      case 'driver_license': return "Driver's License";
      case 'other': return 'Other';
      default: return idType;
    }
  };
  // Process initial data
  useEffect(() => {
    if (data) { 
      // If there is an existing image URL, set the preview
      if (data.idFrontImage && typeof data.idFrontImage === 'string') {
        setIdFrontPreview(ipfsUriToUrl(data.idFrontImage));
      }
      if (data.idBackImage && typeof data.idBackImage === 'string') {
        setIdBackPreview(ipfsUriToUrl(data.idBackImage));
      }
    }
  }, [data]);
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Identity Verification Review
          </Typography>
          {reviewStatus && (
            <Chip 
              label={getStatusText(reviewStatus)} 
              color={getStatusColor(reviewStatus) as any}
              size="small"
            />
          )}
        </Box>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          The following is the identity verification information submitted by the user, please verify it carefully
        </Alert>
        
        {submitting && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Processing...
            </Typography>
          </Box>
        )}
        
        <Grid container spacing={3}>
          {/* Personal Basic Information - Read Only */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Real Name"
              value={data.realName || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Gender"
              value={getGenderText(data.gender || '')}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Age"
              value={data.age?.toString() || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Occupation"
              value={data.occupation || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Address"
              value={data.address || ''}
              fullWidth
              multiline
              rows={2}
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
        
          {/* ID Information - Read Only */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              ID Information
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="ID Type"
              value={getIdTypeText(data.idType || '')}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="ID Number"
              value={data.idNumber || ''}
              fullWidth
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
          
          {/* ID Photo Display - Read Only */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom>
              ID Photos
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Front Photo of ID
              </Typography>
              
              {data.idFrontImage ? (
                <Box sx={{ display: 'inline-block' }}>
                  <ImagePreview
                    src={ipfsUriToUrl(data.idFrontImage)}
                    alt="Front of ID"
                    style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Front photo of ID not uploaded
                </Typography>
              )}
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Back Photo of ID
              </Typography>
              
              {data.idBackImage ? (
                <Box sx={{ display: 'inline-block' }}>
                  <ImagePreview
                    src={ipfsUriToUrl(data.idBackImage)}
                    alt="Back of ID"
                    style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 8 }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Back photo of ID not uploaded
                </Typography>
              )}
            </Box>
          </Grid>
          
          {/* Review Function */}
          {canReview   && (
            <>
              
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                     <Button
                                      variant="contained"
                                      color="success"
                                      onClick={() => handleReset(true)}
                                      disabled={submitting||(isWhitelisted&&status==2)}
                                    >
                                      {submitting ? 'Processing...' : 'Reset'}
                                    </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleReview(false)}
                      disabled={submitting||(isWhitelisted&&status==2)}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleReview(true)}
                       disabled={submitting||(isWhitelisted&&status==2)}
                  >
                    {submitting ? 'Processing...' : 'Approve'}
                  </Button>
                </Box>
              </Grid>
            </>
          )}
          
          {/* Read-only status prompt */}
          {!canReview && (
            <Grid item xs={12}>
              <Alert severity="info">
                You are viewing the identity verification information, which is currently in read-only mode.
              </Alert>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default KYCLevel1ReviewForm;